import { useEffect, useState } from "react";

import { ApiError } from "../../lib/api";
import { signInThisTab, type SpaceEntry } from "../../lib/runtime";
import { forget, heldSubject, LOCAL_PEOPLE } from "../../lib/tabSession";
import { MountSurface } from "./MountSurface";

const RESOLVE_INTERVAL_MS = 5000;
/** How long an incomplete reading stays silent before this page says anything on its own. */
const PRESENCE_GRACE_MS = 3000;

/** The sorted mount id list a set of activities holds, the shared key for "did this change". */
function mountKey(entry: SpaceEntry): string {
  return entry.activities
    .map((one) => `${one.activity_id}:${one.mount_id}`)
    .sort()
    .join(",");
}

/** Whether two readings name the same live mounts, so an unchanged one never remounts a surface. */
function sameMounts(held: SpaceEntry | null, found: SpaceEntry): boolean {
  if (held === null) return false;
  return held.role === found.role && mountKey(held) === mountKey(found);
}

/** The arena surface for whatever a resolved address currently has loaded. */
export function SpacePage({
  resolveKey,
  resolve,
  label,
  kind,
  as,
}: {
  resolveKey: string;
  resolve: () => Promise<SpaceEntry>;
  label: string;
  kind: string;
  /** Who this tab acts as, when the address pinned one. Never what that person may see. */
  as?: string;
}) {
  const [entry, setEntry] = useState<SpaceEntry | null>(null);
  const [drawn, setDrawn] = useState<Record<string, boolean>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [who, setWho] = useState<string | undefined>(heldSubject);
  const [signedOut, setSignedOut] = useState(false);
  const [pinning, setPinning] = useState<boolean>(as !== undefined && as !== heldSubject());
  const [pastGrace, setPastGrace] = useState(false);

  // Every mount reports its own presence once its socket settles, but a mount that never connects
  // may never settle at all. Give the set a few seconds to come in before this page says anything
  // about its own; past that, a still-incomplete reading gets the same quiet note area rather than
  // sitting on a pure black screen indefinitely.
  const mountsKey = entry === null ? null : mountKey(entry);
  useEffect(() => {
    setPastGrace(false);
    if (mountsKey === null) return;
    const timer = window.setTimeout(() => setPastGrace(true), PRESENCE_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [mountsKey]);

  useEffect(() => {
    if (!pinning || as === undefined) return;
    let live = true;
    signInThisTab(as)
      .then(() => {
        if (!live) return;
        setWho(as);
        setPinning(false);
      })
      .catch(() => {
        if (live) setPinning(false);
      });
    return () => {
      live = false;
    };
  }, [as, pinning]);

  useEffect(() => {
    let live = true;
    setEntry(null);
    setProblem(null);

    const read = () => {
      resolve()
        .then((found) => {
          if (!live) return;
          setProblem(null);
          setSignedOut(false);
          setEntry((held) => (sameMounts(held, found) ? held : found));
        })
        .catch((error: unknown) => {
          if (!live) return;
          const status = error instanceof ApiError ? error.status : 0;
          if (status === 401 || status === 403) {
            forget();
            setWho(undefined);
            setSignedOut(true);
            return;
          }
          setProblem(error instanceof Error ? error.message : "cannot open this space");
        });
    };

    read();
    const again = window.setInterval(read, RESOLVE_INTERVAL_MS);
    return () => {
      live = false;
      window.clearInterval(again);
    };
  }, [resolveKey, resolve, who]);

  if (problem !== null) return <Problem label={label} kind={kind} what={problem} />;
  if (pinning) return <Note text={`opening as ${as ?? ""}`} />;
  if (signedOut)
    return (
      <WhoAreYou
        onPicked={(person) => {
          setWho(person);
          setSignedOut(false);
        }}
      />
    );
  if (entry === null) return <Note text="opening" />;

  if (entry.activities.length === 0) {
    return <Note text={`nothing is running in ${entry.name || label} yet`} />;
  }

  // One surface per mount, and the same one for every activity there will ever be. Which of a
  // mount's canvases this person reaches was decided by their role in the cloud; this page reads
  // no manifest, no activity id and no role of its own. Surfaces are quiet: a mount with nothing
  // for this person takes no room, and the page speaks once when none of them draws.
  const allReported = entry.activities.every((one) => drawn[one.mount_id] !== undefined);
  const allQuiet =
    entry.activities.length > 0 &&
    entry.activities.every((one) => drawn[one.mount_id] === false);
  const stillConnecting = pastGrace && !allReported && !allQuiet;
  return (
    <main style={arena}>
      {entry.activities.map((one) => (
        <MountSurface
          key={one.mount_id}
          mountId={one.mount_id}
          activityId={one.activity_id}
          quiet
          onPresence={(draws) => {
            setDrawn((held) =>
              held[one.mount_id] === draws ? held : { ...held, [one.mount_id]: draws },
            );
          }}
        />
      ))}
      {allQuiet && (
        <div style={quietNote}>nothing to watch in {entry.name || label} yet</div>
      )}
      {stillConnecting && <div style={quietNote}>connecting</div>}
      <Who entry={entry} />
    </main>
  );
}

const arena: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  overflowY: "auto",
  background: "#0b0d10",
  color: "rgba(255,255,255,0.85)",
};

const quietNote: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "grid",
  placeItems: "center",
  color: "rgba(255,255,255,0.55)",
  font: "13px ui-monospace, monospace",
};

/** Who this tab is, on a surface that has no bar of its own. Pressing it picks somebody else. */
function Who({ entry }: { entry: SpaceEntry }) {
  return (
    <button
      type="button"
      onClick={() => {
        forget();
        window.location.reload();
      }}
      style={{
        position: "fixed",
        right: 10,
        bottom: 8,
        zIndex: 10,
        padding: "3px 9px",
        cursor: "pointer",
        background: "rgba(11,13,16,0.75)",
        color: "rgba(255,255,255,0.6)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 4,
        font: "12px ui-monospace, monospace",
      }}
    >
      {entry.name || entry.join_code} as {entry.role}
    </button>
  );
}

/** Who this tab is. One address, and the tab holds the answer, so other tabs stay who they were. */
function WhoAreYou({ onPicked }: { onPicked: (subject: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  return (
    <main
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background: "#0b0d10",
        color: "rgba(255,255,255,0.8)",
        font: "15px ui-monospace, monospace",
      }}
    >
      <div style={{ display: "grid", gap: 14, justifyItems: "center", padding: 24 }}>
        <p style={{ margin: 0 }}>who are you?</p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          {LOCAL_PEOPLE.map((person) => (
            <button
              key={person}
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setBusy(person);
                setRefused(null);
                signInThisTab(person)
                  .then(() => {
                    onPicked(person);
                  })
                  .catch((error: unknown) => {
                    setBusy(null);
                    setRefused(error instanceof Error ? error.message : "could not sign in");
                  });
              }}
              style={{
                padding: "14px 22px",
                cursor: busy === null ? "pointer" : "wait",
                background: busy === person ? "#2c3e50" : "#1c2530",
                color: "white",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 6,
                font: "inherit",
              }}
            >
              {person}
            </button>
          ))}
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
          this tab only, so another tab can be somebody else
        </p>
        {refused !== null && <p style={{ margin: 0, color: "#ff8a80" }}>{refused}</p>}
      </div>
    </main>
  );
}

function Note({ text }: { text: string }) {
  return (
    <main
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background: "#0b0d10",
        color: "rgba(255,255,255,0.65)",
        font: "13px ui-monospace, monospace",
      }}
    >
      {text}
    </main>
  );
}

function Problem({ label, kind, what }: { label: string; kind: string; what: string }) {
  return (
    <main style={{ padding: 24, fontFamily: "monospace", color: "#eee", background: "#0b0d10" }}>
      <h1>Cannot open {label}</h1>
      <p>{what}</p>
      <p>A {kind} you are not a member of reads the same as one that does not exist.</p>
      <p>Sign in first at /v1/auth/twitch/start?as=viewer, then reload.</p>
    </main>
  );
}
